import React, { FunctionComponent, ReactNode, useCallback } from 'react'
import classNames from 'classnames'
import { Button, Icon } from '@lightspeed/design-system-react'
import {
  QuickKeyFolder,
  QuickKeyKey,
  QuickKeyRoot,
} from '../../../quick-keys/qk-types'
import { QuickKeyNodeType } from '../../../quick-keys/qk.constants'
import { QKNode } from './QKNode'

interface QKRootProps {
  quickKey: QuickKeyRoot | QuickKeyFolder
  focussed: QuickKeyFolder | QuickKeyKey | null
  setRoot: (node: QuickKeyFolder | null) => void
  setFocus: (node: QuickKeyFolder | QuickKeyKey) => void
  selectQuickKey: (node: QuickKeyFolder | QuickKeyKey) => void
}

interface FolderWrapperProps {
  quickKey: QuickKeyFolder
  setRoot: (node: QuickKeyFolder | null) => void
  setFocus: (node: QuickKeyFolder | QuickKeyKey) => void
  children: ReactNode
}

const FolderWrapper: FunctionComponent<FolderWrapperProps> = ({
  quickKey,
  setRoot,
  setFocus,
  children,
}) => {
  const close = useCallback(() => {
    setRoot(null)
    setFocus(quickKey)
  }, [quickKey, setFocus, setRoot])
  return (
    <div
      className={classNames(
        'vd-quick-key-folder-view',
        `vd-quick-key-folder-view--${quickKey.viewData.color}`
      )}
    >
      <div className="vd-quick-key-folder-header">
        <Button
          onClick={close}
          className="vd-quick-key-folder-action vd-quick-key-folder-action--close vd-pa0"
          data-testid="quick-key-folder-close"
        >
          <Icon icon="close" />
        </Button>
        {quickKey.viewData.label}
      </div>
      {children}
    </div>
  )
}

const Grid: FunctionComponent<QKRootProps> = ({
  quickKey,
  focussed,
  setFocus,
  selectQuickKey,
}) => {
  return (
    <div className="vd-quick-key-grid">
      {quickKey.children.map(page => (
        <div className="vd-quick-key-page" key={page.node.id}>
          {'pageColumns' in page.viewData
            ? page.viewData.pageColumns.map((column, i) => (
                <div className="vd-quick-key-page-column" key={i}>
                  {column.map(quickKey => (
                    <QKNode
                      key={quickKey.node.id}
                      quickKey={quickKey}
                      isFocussed={focussed === quickKey}
                      setFocus={setFocus}
                      selectQuickKey={selectQuickKey}
                    />
                  ))}
                </div>
              ))
            : page.node.id}
        </div>
      ))}
    </div>
  )
}

export const QKRoot: FunctionComponent<QKRootProps> = ({
  quickKey,
  focussed,
  setRoot,
  setFocus,
  selectQuickKey,
}) => {
  switch (quickKey.type) {
    case QuickKeyNodeType.ROOT:
      return (
        <Grid
          quickKey={quickKey}
          focussed={focussed}
          setRoot={setRoot}
          setFocus={setFocus}
          selectQuickKey={selectQuickKey}
        />
      )
    case QuickKeyNodeType.FOLDER:
      return (
        <FolderWrapper
          quickKey={quickKey}
          setRoot={setRoot}
          setFocus={setFocus}
        >
          <Grid
            quickKey={quickKey}
            focussed={focussed}
            setRoot={setRoot}
            setFocus={setFocus}
            selectQuickKey={selectQuickKey}
          />
        </FolderWrapper>
      )
    default:
      return null
  }
}
